import { useState, useEffect, useRef } from 'react';
import { Button } from '@startuptools/ui/Button';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { TemplatesCard } from '../TemplatesCard';
import { type DynamicContent } from './types';

export const MainDynamicDivContent = ({
  buttonNames,
  contentObject,
}: {
  buttonNames: string[];
  contentObject: DynamicContent;
}) => {
  const [activeButton, setActiveButton] = useState(0);
  const [isInteracting, setIsInteracting] = useState(false);
  const [isInView, setIsInView] = useState(false);

  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      // @ts-expect-error TS18048
      setIsInView(entry.isIntersecting);
    });
    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => {
      if (observer) observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (isInView && !isInteracting) {
      const intervalId = setInterval(() => {
        setActiveButton(prevActiveButton => (prevActiveButton + 1) % buttonNames.length);
      }, 5000);
      return () => clearInterval(intervalId);
    }
  }, [isInView, isInteracting, buttonNames.length]);

  const handleButtonClick = (index: number) => {
    setIsInteracting(true);
    setActiveButton(index);
    setTimeout(() => {
      setIsInteracting(false);
    }, 10000);
  };

  const buttons = buttonNames.map((buttonName, index) => {
    const isActive = activeButton === index;
    return isActive ? (
      <Button
        key={buttonName}
        onClick={() => handleButtonClick(index)}
        sx={{ width: { xl: '12rem', lg: '9rem', md: '7rem' } }}
      >
        {buttonName}
      </Button>
    ) : (
      <Button
        key={buttonName}
        variant="outlined"
        onClick={() => handleButtonClick(index)}
        sx={{ width: { xl: '12rem', lg: '9rem', md: '7rem' } }}
      >
        {buttonName}
      </Button>
    );
  });

  const renderContent = () => {
    const activeContent = contentObject[activeButton];
    if (activeContent) {
      return (
        <Stack display={'flex'} direction={'row'} sx={{ paddingTop: '3rem', gap: '5.5rem', minHeight: '11.125rem' }}>
          <TemplatesCard
            buttons={activeContent.buttons}
            title={activeContent.title}
            iconName={activeContent.iconName}
            referToUrl={activeContent.referToUrl}
            showAnchor={activeContent.showAnchor}
          />
          <Typography
            level="body-md"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              padding: '1.5rem auto auto 0rem',
              gap: '0.5rem',
            }}
          >
            {activeContent.description}
          </Typography>
        </Stack>
      );
    }
    return null;
  };

  return (
    <div ref={ref}>
      <Stack>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            display: 'flex',
            padding: '0px',
            gap: '1rem',
            justifyContent: 'space-between',
          }}
        >
          {buttons}
        </Stack>
        {renderContent()}
      </Stack>
    </div>
  );
};
